import React, { useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, ResponseErrorPanel } from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { Box, Button, Tab, Tabs } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import {
  AppEnvironment,
  ENV_LABEL,
  entityEnvironments,
  envLinks,
  resolveNamespace,
} from '../catalog/appEnvironments';

// Logs tab: the entity's pod logs from Grafana, scoped to its k8s namespace
// (bootstrap convention: namespace == service name), for each environment
// the app runs in (`sparrow.io/environments`; staging-only when absent).
//
// Staging embeds the shared `app-logs` dashboard on the staging Grafana with
// the BU's staging Loki. Production uses the production Grafana with the BU's
// production Loki: the app-logs dashboard when one is configured there,
// otherwise Grafana Explore with `{namespace="<app>"}`. Datasources per BU
// and environment come from `sparrow.<bu>.<env>.logsDatasource`; see
// ../catalog/appEnvironments.ts. "Open in Grafana" is always offered in case
// the embedded view asks for a login.

export const EntityLogsIframe = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const envs = entityEnvironments(entity);
  const [env, setEnv] = useState<AppEnvironment>(envs[0]);
  const namespace = resolveNamespace(entity);
  const links = envLinks(config, entity, env);

  return (
    <InfoCard
      title={`Logs — ${namespace}${envs.length > 1 ? '' : ` (${ENV_LABEL[env]})`}`}
      noPadding
      action={
        links.logs ? (
          <Box pr={2} pt={1}>
            <Button
              size="small"
              color="primary"
              href={links.logs}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              Open in Grafana
            </Button>
          </Box>
        ) : undefined
      }
    >
      {envs.length > 1 && (
        <Tabs
          value={env}
          onChange={(_, value: AppEnvironment) => setEnv(value)}
          indicatorColor="primary"
          textColor="primary"
        >
          {envs.map(e => (
            <Tab key={e} value={e} label={ENV_LABEL[e]} />
          ))}
        </Tabs>
      )}
      {links.logsEmbed ? (
        <iframe
          key={env}
          src={links.logsEmbed}
          title={`${ENV_LABEL[env]} logs for ${namespace}`}
          style={{ width: '100%', height: '80vh', border: 0 }}
        />
      ) : (
        <Box p={2}>
          <ResponseErrorPanel
            error={new Error(links.logsMissing ?? `No ${env} logs are configured.`)}
          />
        </Box>
      )}
    </InfoCard>
  );
};

export default EntityLogsIframe;
